<template>
    <div>
        <div class="mt-5 flex flex-col md:flex-row justify-between items-start">
            <div class="flex flex-col items-center md:items-start w-full">
                <h1 class="text-3xl text-center md:text-left">
                    {{
                        slug
                            ? "Detalle de configuración logística"
                            : "Nueva configuración logística"
                    }}
                    <strong v-if="slug" class="text-theme-primary">
                        #{{ slug }}
                    </strong>
                </h1>
            </div>
            <div
                v-if="slug"
                class="flex flex-col mt-2 md:mt-0 md:min-w-max justify-center text-center"
            >
                <small class="text-theme-muted">
                    📆💾 {{ itemComponent.createdAt | date }}
                </small>
                <small class="text-theme-muted">
                    📆✏ {{ itemComponent.updatedAt | date }}
                </small>
            </div>
        </div>

        <div class="my-4">
            <custom-navbar
                id="tabs-order"
                v-model="activeTab"
                center-collapsed
                not-line
            >
                <template #left>
                    <custom-navbar-item id="home">
                        <router-link
                            to="/app/logistic-configurations"
                            class="rounded-full bg-brand-blue flex justify-center items-center p-2"
                        >
                            <span>🏠</span>
                        </router-link>
                    </custom-navbar-item>
                    <custom-navbar-item
                        id="general"
                        :active="['general', 'home'].includes(activeTab)"
                        class="px-2 md:px-4 xl:px-6"
                    >
                        <div class="flex">
                            <span class="lg:block hidden"> General </span>
                            <span>&nbsp;⚙️</span>
                        </div>
                    </custom-navbar-item>
                    <custom-navbar-item
                        v-if="slug"
                        id="simulate"
                        :active="activeTab === 'simulate'"
                        class="px-2 md:px-4 xl:px-6"
                    >
                        <div class="flex">
                            <span class="lg:block hidden"> Simulación </span>
                            <span>&nbsp;🔬</span>
                        </div>
                    </custom-navbar-item>
                </template>
                <template #right>
                    <div class="hidden md:block">
                        <custom-switch
                            v-if="
                                $ability.hasPermission(
                                    'logisticConfiguration:Update'
                                ) && slug
                            "
                            v-model="enabledUpdate"
                        >
                            <span class="text-theme-inverse">🖊</span>
                        </custom-switch>
                    </div>
                </template>
            </custom-navbar>
        </div>

        <div
            v-if="
                $ability.hasPermission('logisticConfiguration:Update') && slug
            "
            class="flex justify-center"
        >
            <div class="block md:hidden my-3">
                <custom-switch v-model="enabledUpdate">
                    <span class="text-theme-inverse">🖊</span>
                </custom-switch>
            </div>
        </div>

        <div v-show="['general', 'home'].includes(activeTab)">
            <p class="mt-3 text-theme-muted text-sm">
                La configuración logística determina los tiempos de entrega de
                una ordén dependiendo de los productos que tenga dicha orden.
            </p>

            <div class="flex flex-wrap mt-4">
                <div class="w-full md:w-1/2 mt-5 px-2">
                    <custom-input
                        v-model="itemComponent.name"
                        label="Nombre"
                        aria-label="Nombre de configuración lógistica"
                        :disabled="!enabledUpdate"
                        :error="
                            !isValidName & introValidators.name
                                ? 'Nombre de configuración logística invalida'
                                : ''
                        "
                        @blur="blurName"
                    />
                </div>
                <div class="w-full md:w-1/2 mt-5 px-2">
                    <custom-input
                        v-model="itemComponent.slug"
                        label="Slug"
                        aria-label="Slug de configuración lógistica"
                        :disabled="!enabledUpdate"
                        :error="
                            !isValidSlug & introValidators.slug
                                ? 'Slug de configuración logística invalida'
                                : ''
                        "
                        @blur="introValidators.slug = true"
                    />
                </div>

                <div class="w-full mt-6 px-2">
                    <h4 class="text-base font-semibold text-theme-base mb-2">
                        Tiempo estimado de preparación
                    </h4>
                    <prepare-time-manage
                        v-model="itemComponent.prepareTime"
                        :enabled="enabledUpdate"
                    />
                </div>

                <div class="w-full mt-6 px-2">
                    <h4 class="text-base font-semibold text-theme-base mb-2">
                        Tiempo estimado de preparación — inventario siempre
                        activo
                    </h4>
                    <prepare-time-manage
                        v-model="itemComponent.alwaysStockPrepareTime"
                        :enabled="enabledUpdate"
                    />
                </div>

                <div class="w-full mt-6 px-2">
                    <h4 class="text-base font-semibold text-theme-base mb-2">
                        Tiempos y costos de entrega por ciudad
                    </h4>
                    <delivery-configurations-by-city-manage
                        v-model="itemComponent.deliveryConfigurationsByCityCode"
                        :enabled="enabledUpdate"
                    />
                </div>

                <div
                    v-if="enabledUpdate"
                    class="w-full mt-8 flex justify-center"
                >
                    <app-button
                        v-if="!slug"
                        size="large"
                        @click="createLogisticConfigurationComponent"
                    >
                        Crear configuración logística
                    </app-button>
                    <app-button
                        v-else
                        size="large"
                        @click="updateLogisticConfigurationComponent"
                    >
                        Actualizar configuración logística
                    </app-button>
                </div>
            </div>
        </div>

        <div v-if="activeTab === 'simulate'" class="mt-3">
            <delivery-simulator :logistic-configuration-slug="slug" />
        </div>
    </div>
</template>

<script>
import tabQuerySyncMixin from "@/mixins/tabQuerySyncMixin";
import { mapActions, mapGetters } from "vuex";

import { slugRegex, titleRegex } from "@/common/lib/regex";
import { slugify } from "@/common/lib/transformers";

import DeliverySimulator from "@/components/logistic-configurations/DeliverySimulator.vue";
import CustomInput from "@/components/utils/CustomInput.vue";
import DeliveryConfigurationsByCityManage from "@/components/utils/DeliveryConfigurationsByCityManage.vue";
import PrepareTimeManage from "@/components/utils/PrepareTimeManage.vue";

export default {
    name: "SupplyChainDetails",
    components: {
        PrepareTimeManage,
        DeliveryConfigurationsByCityManage,
        CustomInput,
        DeliverySimulator
    },
    mixins: [tabQuerySyncMixin],
    data: () => ({
        slug: "",
        activeTab: "general",
        $tabsWhitelist: ["general", "simulate"],
        loadItem: false,
        isCreation: true,
        isLoading: false,
        enabledUpdate: false,
        introValidators: { slug: false, name: false },
        itemComponent: {
            slug: "",
            name: "",
            createdAt: "",
            updatedAt: "",
            prepareTime: { minutes: 0, hours: 1, days: 0, months: 0 },
            alwaysStockPrepareTime: {
                minutes: 0,
                hours: 1,
                days: 0,
                months: 0
            },
            deliveryConfigurationsByCityCode: [
                {
                    cityCode: "default",
                    configuration: [
                        { deliveryHours: 1, deliveryPrice: 1000, min: 1 }
                    ]
                }
            ]
        }
    }),
    computed: {
        ...mapGetters("control", ["backgroundColor"]),
        isValidName() {
            return titleRegex.test(this.itemComponent.name);
        },
        isValidSlug() {
            return slugRegex.test(this.itemComponent.slug);
        }
    },
    watch: {},
    async mounted() {
        const verifyPagePermission =
            this.$ability.verifyPagePermission.bind(this);
        this.isCreation = true;
        if ("slug" in this.$route.params) {
            this.isCreation = false;
            const slug = this.$route.params.slug;
            this.slug = slug;
            await this.getBaseItem(slug);
        } else if ("slugClone" in this.$route.params) {
            if (
                !(await verifyPagePermission(
                    "logisticConfiguration:Create",
                    "/app/logistic-configurations"
                ))
            )
                return;
            this.enabledUpdate = true;
            const slugTmp = this.$route.params.slugClone;
            this.slug = `${slugTmp}-clone`;
            await this.getBaseItem(slugTmp);
            this.itemComponent = {
                ...this.itemComponent,
                name: `${this.itemComponent.name} CLONE`,
                slug: this.slug,
                active: false
            };
            this.$swal.fire({
                background: this.backgroundColor,
                title: "Estas clonando una configuración logística",
                icon: "info",
                showCloseButton: true
            });
        } else {
            if (
                !(await verifyPagePermission(
                    "logisticConfiguration:Create",
                    "/app/logistic-configurations"
                ))
            )
                return;
            this.enabledUpdate = true;
            this.loadItem = true;
        }
    },
    methods: {
        ...mapActions("logisticConfigurations", [
            "getLogisticConfigurationBySlug",
            "updateLogisticConfiguration",
            "createLogisticConfiguration"
        ]),
        async getBaseItem(slug) {
            this.isLoading = true;
            this.$loading.show();
            try {
                this.itemComponent = await this.getLogisticConfigurationBySlug({
                    slug
                });
                this.loadItem = true;
                this.isLoading = false;
                this.$loading.hide();
            } catch (error) {
                this.isLoading = false;
                this.$loading.hide();
                this.isLoading = false;
                await this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
                if (error.statusCode === 404) {
                    this.$router.push("/app/logistic-configurations");
                }
            }
        },
        async updateLogisticConfigurationComponent() {
            if (!this.validateData()) {
                return;
            }
            this.isLoading = true;
            this.$loading.show();
            try {
                await this.updateLogisticConfiguration(this.getDataSend());
                this.$sound.play();
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: "Actualización exitosa",
                    icon: "success",
                    showCloseButton: true,
                    timer: 3000
                });
                this.$loading.hide();
                this.isLoading = false;
            } catch (error) {
                this.$loading.hide();
                this.isLoading = false;
                this.$loading.hide();
                this.$sound.play("error");
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        },
        async createLogisticConfigurationComponent() {
            if (!this.validateData()) {
                return;
            }
            this.isLoading = true;
            this.$loading.show();
            try {
                const data = await this.createLogisticConfiguration(
                    this.getDataSend()
                );
                this.$loading.hide();
                this.itemComponent = { ...data, ...this.itemComponent };
                this.$sound.play();
                const dialogResult = await this.$swal.fire({
                    background: this.backgroundColor,
                    title: "Creación exitosa",
                    text: `Slug: ${data.slug}`,
                    icon: "success",
                    showDenyButton: true,
                    showCloseButton: true,
                    showCancelButton: true,
                    confirmButtonColor: "#02672D",
                    cancelButtonColor: "#253C99",
                    denyButtonColor: "#d33ddd",
                    confirmButtonText: "Ver detalle",
                    denyButtonText: "Ir a lista de configuraciones logísticas",
                    cancelButtonText: "Nueva configuración logística"
                });
                if (dialogResult.isConfirmed) {
                    this.slug = data.slug;
                    this.isCreation = false;
                    this.itemComponent = { ...this.itemComponent, ...data };
                    this.$router.push(
                        `/app/logistic-configurations/detail/${this.slug}`
                    );
                }
                if (dialogResult.isDenied) {
                    this.$router.push("/app/logistic-configurations");
                }
                if (dialogResult.isDismissed) {
                    this.introValidators = {
                        slug: false,
                        name: false,
                        summary: false
                    };
                    this.itemComponent = {
                        slug: "",
                        name: "",
                        createdAt: "",
                        updatedAt: "",
                        coordinates: {
                            latitude: 4.63082,
                            longitude: -74.15035
                        },
                        prepareTime: {
                            minutes: 0,
                            hours: 1,
                            days: 0,
                            months: 0
                        },
                        alwaysStockPrepareTime: {
                            minutes: 0,
                            hours: 1,
                            days: 0,
                            months: 0
                        },
                        deliveryConfigurationsByCityCode: {
                            cityCode: "11001",
                            configuration: {
                                1: { deliveryHours: 0, deliveryPrice: 0 }
                            }
                        }
                    };
                }
                this.isLoading = false;
            } catch (error) {
                this.isLoading = false;
                this.isLoading = false;
                this.$loading.hide();
                this.$sound.play("error");
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        },
        async setSlug() {
            if (this.isValidName && this.isCreation) {
                const dialogResult = await this.$swal.fire({
                    background: this.backgroundColor,
                    title: "Quieres cambiar el slug ?",
                    icon: "info",
                    showCancelButton: true,
                    confirmButtonColor: "#02913f",
                    cancelButtonColor: "#3085d6",
                    confirmButtonText: "Si",
                    cancelButtonText: "No"
                });
                if (dialogResult.isConfirmed) {
                    this.itemComponent.slug = slugify(this.itemComponent.name);
                }
            }
        },
        blurName() {
            this.introValidators.name = true;
            this.setSlug();
        },
        validateData() {
            this.introValidators = {
                slug: true,
                name: true
            };
            const toComplete = [];
            if (!this.isValidSlug) {
                toComplete.push("Slug");
            }

            if (!this.isValidName) {
                toComplete.push("Nombre");
            }

            if (toComplete.length) {
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: "Configuración logística invalida",
                    text: `Revisa por favor que todo este completo. Valida los siguientes campos: ${toComplete.join(
                        ", "
                    )}`,
                    icon: "info"
                });
                return false;
            }
            return true;
        },
        getDataSend() {
            return {
                ...this.itemComponent
            };
        }
    }
};
</script>
